import { useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Textarea,
} from '@fluentui/react-components';
import { Unlock } from 'lucide-react';

import { CanaryReleaseResult, canaryErrorCode, releaseCanary } from '../api/canary';
import { Migration } from '../api/migrations';
import RelativeTime from './RelativeTime';
import { errorText } from '../lib/errorText';
import { STROKE, TEXT } from '../theme/tokens';

import EnvironmentEcho from './EnvironmentEcho';
// FD-372 (FD-ADR-035) — the first-device canary hold, inside the Wave panel.
//
// Design direction: an INLINE STATE ROW, the WavePackagePanel precedent — this
// is not a feature area of its own, it is the third piece of "is this wave
// deliverable / delivering": credential, package, canary. The state must be
// legible at rest, because what the hold is doing is invisible from the device
// grid (held devices just look pending) and an operator who cannot see the
// hold will read it as a stuck wave.
//
// Three states while a wave runs with canary mode on:
//
//   armed      — no device has fetched a plan yet. The FIRST to fetch becomes
//                the canary; every other device holds at 409 CANARY_HOLD.
//   in flight  — the canary is stamped and migrating; other devices held.
//   released   — an operator released the hold (the audited FD-ADR-027-style
//                override). The canary's own outcome is NOT rewritten.
//
// The release requires a reason and is attributed server-side from the
// EasyAuth principal — the same discipline as the readiness override, for the
// same reason: releasing a hold whose canary salvaged/faulted is a judgement
// call that must leave a decision trail.

export interface WaveCanaryPanelProps {
  migration: Migration;
  /** Refetch the migration — canary state changed. */
  onReleased: () => void;
}

const MIN_REASON = 4;

export default function WaveCanaryPanel({ migration, onReleased }: WaveCanaryPanelProps) {
  const [releaseOpen, setReleaseOpen] = useState(false);

  // A pre-FD-371 Manager omits the field entirely; 'off' and null render
  // nothing. Only an armed canary wave has state worth a row.
  if (migration.canaryMode !== 'on') return null;

  const released = Boolean(migration.canaryReleasedUtc);
  const serial = migration.canarySerial;
  // Devices held behind the canary. deviceCount includes the canary itself.
  const heldOthers = Math.max((migration.deviceCount ?? 0) - 1, 0);

  return (
    <div
      className="flex flex-col gap-2 border-t pt-4"
      style={{ borderColor: STROKE.base }}
      aria-label="Canary"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h3
            className="font-display text-[13px] font-semibold tracking-tight"
            style={{ color: TEXT.primary }}
          >
            Canary
          </h3>
          {released ? (
            <Badge appearance="tint" color="subtle">
              Hold released
            </Badge>
          ) : serial ? (
            <Badge appearance="tint" color="informative">
              Canary in flight
            </Badge>
          ) : (
            <Badge appearance="tint" color="warning">
              Armed
            </Badge>
          )}
        </div>
        {!released && (
          <Button
            appearance="secondary"
            size="small"
            icon={<Unlock size={14} strokeWidth={1.5} />}
            onClick={() => setReleaseOpen(true)}
          >
            Release canary hold…
          </Button>
        )}
      </div>

      {released ? (
        <p className="font-display text-[13px]" style={{ color: TEXT.secondary }}>
          Hold released by{' '}
          <span className="font-mono text-xs">{migration.canaryReleasedBy ?? 'unknown'}</span>{' '}
          <RelativeTime iso={migration.canaryReleasedUtc!} /> — every device can fetch its
          plan. The canary&rsquo;s outcome stays recorded on its device row.
        </p>
      ) : serial ? (
        <p className="font-display text-[13px]" style={{ color: TEXT.secondary }}>
          <span className="font-mono text-xs" style={{ color: TEXT.primary }}>
            {serial}
          </span>{' '}
          is migrating first.{' '}
          {heldOthers === 1 ? '1 other device is' : `${heldOthers} other devices are`} held
          until it completes — a failed canary keeps the wave held, deliberately.
        </p>
      ) : (
        <p className="font-display text-[13px]" style={{ color: TEXT.secondary }}>
          The first device to fetch its flight plan becomes the canary; every other device is
          held until it completes.
        </p>
      )}

      {/* Mounted CLOSED and toggled open — a Fluent Dialog mounted already-open
          never enters the accessibility tree (FLUENT_UI_ADDENDUM). */}
      <CanaryReleaseDialog
        open={releaseOpen}
        migrationId={migration.migrationId}
        canarySerial={serial ?? null}
        onClose={() => setReleaseOpen(false)}
        onDone={onReleased}
      />
    </div>
  );
}

// ——— release dialog ————————————————————————————————————————————————————————

function CanaryReleaseDialog({
  open,
  migrationId,
  canarySerial,
  onClose,
  onDone,
}: {
  open: boolean;
  migrationId: string;
  canarySerial: string | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const [isSubmitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<CanaryReleaseResult | null>(null);
  const [error, setError] = useState<unknown>(null);

  // Reset every piece of local state when the dialog closes, so reopening never
  // shows a previous run's result or a stale reason.
  useEffect(() => {
    if (!open) {
      setReason('');
      setSubmitting(false);
      setResult(null);
      setError(null);
    }
  }, [open]);

  const reasonInvalid = reason.trim().length < MIN_REASON;
  const notActive = canaryErrorCode(error) === 'CANARY_NOT_ACTIVE';

  async function submit() {
    if (isSubmitting || reasonInvalid) return;
    setSubmitting(true);
    setError(null);
    try {
      setResult(await releaseCanary(migrationId, reason.trim()));
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  }

  function close() {
    // The panel's state changed on success — and on CANARY_NOT_ACTIVE too: the
    // row the button was rendered from is stale, and refetching is what makes
    // the button stop being offered.
    const changed = result !== null || notActive;
    onClose();
    if (changed) onDone();
  }

  return (
    <Dialog open={open} onOpenChange={(_, d) => !d.open && close()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Release canary hold</DialogTitle>
          <DialogContent>
            <div className="flex flex-col gap-4">
              {/* FD-422 — NOT in the task's list, added because the enumeration
                  was done from the code rather than from the list. Releasing the
                  hold lets every held device in one deployment fetch a plan
                  carrying a tenant-join credential, and it is audited against
                  this operator — the same shape as the dialogs that were named. */}
              <EnvironmentEcho />

              {result ? (
                <MessageBar intent="success">
                  <MessageBarBody>
                    <MessageBarTitle>
                      {result.repeat ? 'Hold was already released' : 'Canary hold released'}
                    </MessageBarTitle>
                    Released by <span className="font-mono text-xs">{result.releasedBy}</span>{' '}
                    at <span className="font-mono text-xs">{result.releasedUtc}</span>. Every
                    held device can now fetch its flight plan.
                  </MessageBarBody>
                </MessageBar>
              ) : (
                <>
                  <MessageBar intent="warning">
                    <MessageBarBody>
                      <MessageBarTitle>This waives the wave&rsquo;s first-device check</MessageBarTitle>
                      Every held device will fetch its flight plan without waiting for
                      {canarySerial ? (
                        <>
                          {' '}
                          the canary (<span className="font-mono text-xs">{canarySerial}</span>)
                        </>
                      ) : (
                        ' a canary'
                      )}{' '}
                      to complete. The release is recorded with who, when, and the reason
                      below; the canary&rsquo;s own outcome is not rewritten.
                    </MessageBarBody>
                  </MessageBar>

                  <Field
                    label="Reason"
                    required
                    hint="Recorded on the canary.released event — why is it safe to proceed without the canary?"
                  >
                    <Textarea
                      value={reason}
                      onChange={(_, d) => setReason(d.value)}
                      placeholder="e.g. Canary salvage traced to a device-local disk fault; wave is healthy"
                      disabled={isSubmitting}
                      resize="vertical"
                    />
                  </Field>

                  {error != null &&
                    (notActive ? (
                      // 409 CANARY_NOT_ACTIVE — not a failure. The hold this
                      // button was rendered for no longer exists (already
                      // released, or canary mode is off). Say so plainly and
                      // refetch on close.
                      <MessageBar intent="info">
                        <MessageBarBody>
                          <MessageBarTitle>Nothing to release</MessageBarTitle>
                          This wave has no canary hold — it may already be released, or
                          canary mode is off. Close to refresh the panel.
                        </MessageBarBody>
                      </MessageBar>
                    ) : (
                      <MessageBar intent="error">
                        <MessageBarBody>
                          <MessageBarTitle>Could not release the hold</MessageBarTitle>
                          {errorText(error)}
                        </MessageBarBody>
                      </MessageBar>
                    ))}
                </>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            {result || notActive ? (
              <Button appearance="primary" onClick={close}>
                Close
              </Button>
            ) : (
              <>
                <Button appearance="secondary" onClick={close} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button
                  appearance="primary"
                  onClick={submit}
                  disabled={reasonInvalid || isSubmitting}
                >
                  {isSubmitting ? 'Releasing…' : 'Release hold'}
                </Button>
              </>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
